import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  Globe, ArrowLeft, Clock, MousePointer, 
  Link as LinkIcon, Eye, ArrowRight, Brain,
  CheckCircle, MessageSquare, Camera, FileText,
  AlertCircle, BookOpen, List, Send, ExternalLink
} from 'lucide-react';
import ChatBot from '../../../components/ChatBot';

const translations = {
  es: {
    title: 'Navegador de Práctica',
    subtitle: 'Completa tareas prácticas en el Portal de Azure',
    stats: {
      timeSpent: 'Tiempo en página',
      clicks: 'Clics realizados',
      links: 'Enlaces visitados',
      scrollDepth: 'Profundidad de scroll',
      completedTasks: 'Tareas completadas'
    },
    actions: {
      back: 'Volver al Dashboard',
      startTask: 'Comenzar tarea',
      endTask: 'Finalizar tarea',
      viewReport: 'Ver reporte',
      takeScreenshot: 'Capturar pantalla',
      addNote: 'Añadir nota',
      submitTask: 'Enviar tarea',
      askMentor: 'Preguntar al mentor'
    },
    tracking: {
      started: 'Seguimiento iniciado',
      ended: 'Seguimiento finalizado',
      report: 'Reporte de actividad'
    },
    activities: {
      title: 'Actividades a completar',
      validation: {
        title: 'Preguntas de validación',
        submit: 'Enviar respuestas'
      },
      notes: {
        placeholder: 'Añade notas sobre la tarea...',
        save: 'Guardar nota'
      }
    },
    mentor: {
      tips: 'Consejos del mentor IA',
      hint: 'Sugerencia'
    }
  },
  en: {
    title: 'Practice Browser',
    subtitle: 'Complete practical tasks in the Azure Portal',
    stats: {
      timeSpent: 'Time spent',
      clicks: 'Clicks made',
      links: 'Links visited',
      scrollDepth: 'Scroll depth',
      completedTasks: 'Tasks completed'
    },
    actions: {
      back: 'Back to Dashboard',
      startTask: 'Start task',
      endTask: 'End task',
      viewReport: 'View report',
      takeScreenshot: 'Take screenshot',
      addNote: 'Add note',
      submitTask: 'Submit task',
      askMentor: 'Ask mentor'
    },
    tracking: {
      started: 'Tracking started',
      ended: 'Tracking ended',
      report: 'Activity report'
    },
    activities: {
      title: 'Activities to complete',
      validation: {
        title: 'Validation questions',
        submit: 'Submit answers'
      },
      notes: {
        placeholder: 'Add notes about the task...',
        save: 'Save note'
      }
    },
    mentor: {
      tips: 'AI Mentor Tips',
      hint: 'Hint'
    }
  }
};

const mockActivities = [
  {
    id: 1,
    title: 'Create a Resource Group',
    description: 'Create a new resource group in Azure Portal following the naming conventions.',
    completed: false,
    validationQuestions: [
      {
        id: 'q1',
        question: 'What naming convention did you use for the resource group?',
        required: true
      },
      {
        id: 'q2',
        question: 'Which region did you select and why?',
        required: true
      }
    ]
  },
  {
    id: 2,
    title: 'Configure Tags',
    description: 'Add appropriate tags to the resource group for better organization.',
    completed: false,
    validationQuestions: [
      {
        id: 'q3',
        question: 'What tags did you add to the resource group?',
        required: true
      },
      {
        id: 'q4',
        question: 'How do these tags help with resource organization?',
        required: true
      }
    ]
  },
  {
    id: 3,
    title: 'Set up Access Control',
    description: 'Configure RBAC for the resource group.',
    completed: false,
    validationQuestions: [
      {
        id: 'q5',
        question: 'Which roles did you assign and to whom?',
        required: true
      },
      {
        id: 'q6',
        question: 'Explain your role assignment strategy.',
        required: true
      }
    ]
  }
];

const mentorTips = [
  {
    id: 1,
    tip: 'Remember to use a consistent naming convention for all resources. A good format is: <project>-<environment>-<region>-<resource>',
    type: 'best-practice'
  },
  {
    id: 2,
    tip: 'Always add at minimum these tags: Owner, Environment, Project, CostCenter',
    type: 'organization'
  },
  {
    id: 3,
    tip: 'Follow the principle of least privilege when assigning roles',
    type: 'security'
  }
];

const BrowserPage = () => {
  const [isTracking, setIsTracking] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [clicks, setClicks] = useState(0);
  const [linksVisited, setLinksVisited] = useState(0);
  const [scrollDepth, setScrollDepth] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [activities, setActivities] = useState(mockActivities);
  const [currentNote, setCurrentNote] = useState('');
  const [notes, setNotes] = useState<{id: number, text: string, timestamp: string}[]>([]);
  const [showChatbot, setShowChatbot] = useState(false);
  const [validationAnswers, setValidationAnswers] = useState<Record<string, string>>({});
  const [screenshots, setScreenshots] = useState<string[]>([]);
  
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language];

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isTracking && startTime) {
      timer = setInterval(() => {
        setTimeSpent(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isTracking, startTime]);

  const handleStartTracking = () => {
    setIsTracking(true);
    setStartTime(Date.now());
    setTimeSpent(0);
    setClicks(0);
    setLinksVisited(0);
    setScrollDepth(0);
  };

  const handleEndTracking = () => {
    setIsTracking(false);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleActivityComplete = (activityId: number) => {
    setActivities(activities.map(activity => 
      activity.id === activityId ? { ...activity, completed: true } : activity
    ));
  };

  const handleAddNote = () => {
    if (currentNote.trim()) {
      setNotes([
        ...notes,
        {
          id: Date.now(),
          text: currentNote,
          timestamp: new Date().toISOString()
        }
      ]);
      setCurrentNote('');
    }
  };

  const handleValidationAnswer = (questionId: string, answer: string) => {
    setValidationAnswers({
      ...validationAnswers,
      [questionId]: answer
    });
  };

  const handleTakeScreenshot = () => {
    const mockScreenshot = `screenshot-${Date.now()}.png`;
    setScreenshots([...screenshots, mockScreenshot]);
  };

  const openAzurePortal = () => {
    window.open('https://portal.azure.com', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dcvee/dashboard')}
              className="flex items-center text-foreground/70 hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {t.actions.back}
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
              <p className="text-foreground/70">{t.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowChatbot(true)}
              className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t.actions.askMentor}
            </button>
            {!isTracking ? (
              <button
                onClick={handleStartTracking}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90"
              >
                {t.actions.startTask}
              </button>
            ) : (
              <button
                onClick={handleEndTracking}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-500 hover:bg-red-600"
              >
                {t.actions.endTask}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="dark-card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground/70">{t.stats.timeSpent}</p>
                    <h3 className="text-xl font-bold text-foreground mt-1">{formatTime(timeSpent)}</h3>
                  </div>
                  <Clock className="h-6 w-6 text-accent" />
                </div>
              </div>

              <div className="dark-card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground/70">{t.stats.clicks}</p>
                    <h3 className="text-xl font-bold text-foreground mt-1">{clicks}</h3>
                  </div>
                  <MousePointer className="h-6 w-6 text-accent" />
                </div>
              </div>

              <div className="dark-card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground/70">{t.stats.links}</p>
                    <h3 className="text-xl font-bold text-foreground mt-1">{linksVisited}</h3>
                  </div>
                  <LinkIcon className="h-6 w-6 text-accent" />
                </div>
              </div>

              <div className="dark-card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground/70">{t.stats.scrollDepth}</p>
                    <h3 className="text-xl font-bold text-foreground mt-1">{scrollDepth}%</h3>
                  </div>
                  <Eye className="h-6 w-6 text-accent" />
                </div>
              </div>
            </div>

            <div className="dark-card">
              <div className="border-b border-border p-4">
                <div className="flex items-center space-x-4">
                  <div className="flex-1 bg-secondary/30 rounded-lg px-4 py-2 flex items-center">
                    <Globe className="h-4 w-4 text-foreground/50 mr-2" />
                    <span className="text-foreground/70">portal.azure.com</span>
                  </div>
                  <button
                    onClick={handleTakeScreenshot}
                    className="p-2 text-foreground/70 hover:text-foreground"
                    title={t.actions.takeScreenshot}
                  >
                    <Camera className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="h-[600px] p-6 flex flex-col items-center justify-center text-center">
                <Globe className="h-16 w-16 text-accent mb-4" />
                <h3 className="text-xl font-medium text-foreground mb-2">Azure Portal</h3>
                <p className="text-foreground/70 mb-6 max-w-md">
                  Complete your Azure tasks in a new tab while we track your progress. Your activity will continue to be monitored for assessment.
                </p>
                <button
                  onClick={openAzurePortal}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-accent hover:bg-accent/90"
                >
                  Open Azure Portal
                  <ExternalLink className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="dark-card p-6">
              <h2 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <List className="h-5 w-5 text-accent mr-2" />
                {t.activities.title}
              </h2>
              <div className="space-y-4">
                {activities.map((activity) => (
                  <div key={activity.id} className="border-b border-border last:border-0 pb-4 last:pb-0">
                    <div className="flex items-start">
                      <div className={`h-5 w-5 rounded-full flex items-center justify-center ${
                        activity.completed
                          ? 'bg-green-500 text-white'
                          : 'border-2 border-border'
                      }`}>
                        {activity.completed && <CheckCircle className="h-3 w-3" />}
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-foreground">{activity.title}</h3>
                        <p className="text-sm text-foreground/70 mt-1">{activity.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dark-card p-6">
              <h2 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <FileText className="h-5 w-5 text-accent mr-2" />
                {t.activities.validation.title}
              </h2>
              <div className="space-y-4">
                {activities.flatMap(activity => 
                  activity.validationQuestions.map(question => (
                    <div key={question.id} className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        {question.question}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <textarea
                        value={validationAnswers[question.id] || ''}
                        onChange={(e) => handleValidationAnswer(question.id, e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                        rows={3}
                      />
                    </div>
                  ))
                )}
                <button className="w-full mt-4 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                  <Send className="h-4 w-4 mr-2" />
                  {t.activities.validation.submit}
                </button>
              </div>
            </div>

            <div className="dark-card p-6">
              <h2 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <BookOpen className="h-5 w-5 text-accent mr-2" />
                {t.actions.addNote}
              </h2>
              <div className="space-y-4">
                <textarea
                  value={currentNote}
                  onChange={(e) => setCurrentNote(e.target.value)}
                  placeholder={t.activities.notes.placeholder}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                  rows={4}
                />
                <button
                  onClick={handleAddNote}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90"
                >
                  {t.activities.notes.save}
                </button>
                {notes.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {notes.map(note => (
                      <div key={note.id} className="bg-secondary/30 rounded-lg p-3">
                        <p className="text-sm text-foreground">{note.text}</p>
                        <p className="text-xs text-foreground/70 mt-1">
                          {new Date(note.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="dark-card p-6">
              <h2 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Brain className="h-5 w-5 text-accent mr-2" />
                {t.mentor.tips}
              </h2>
              <div className="space-y-4">
                {mentorTips.map(tip => (
                  <div key={tip.id} className="bg-accent/5 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-accent mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-foreground/90">{tip.tip}</p>
                        <span className="text-xs text-accent mt-1 block">{t.mentor.hint}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role="azure-admin"
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowserPage;